// src/pages/CampanhaAnalise.jsx
//
// Tela de ANÁLISE INDIVIDUAL de uma campanha.
// Rota: /campanhas/:external_id/analise
//
// Endpoints consumidos:
//   - GET  /analise/campaign-data/{external_id}    — dados completos
//   - POST /analise/refresh/{external_id}          — força sync agora
//   - POST /analise/notes                          — salva nota do dia
//
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Search,
  MapPin,
  Smartphone,
  Users,
  Clock,
  History,
  ChevronDown,
  ChevronUp,
  Edit3,
  ExternalLink,
} from "lucide-react";
import { toast } from "react-toastify";
import analiseApi from "../services/analiseApiService";

// ============================================================================
// Helpers de formatação
// ============================================================================

const formatCurrency = (v, code = "BRL") => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: code || "BRL",
  }).format(v);
};

const formatInt = (v) => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("pt-BR").format(Math.round(v));
};

const formatPct = (v) => {
  if (v == null || isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
};

const formatDec = (v, digits = 2) => {
  if (v == null || isNaN(v)) return "—";
  return v.toFixed(digits);
};

const formatRelativeTime = (ms) => {
  if (!ms) return "nunca";
  const diffMs = Date.now() - ms;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "agora há pouco";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `há ${diffD}d`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
};

const formatDateTime = (ms) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ============================================================================
// Dicionários de tradução
// ============================================================================

const statusLabel = (status) => {
  const map = {
    ENABLED: "Ativa",
    PAUSED: "Pausada",
    REMOVED: "Removida",
  };
  return map[status] || status;
};

const biddingStrategyLabel = (strategy) => {
  const map = {
    MAXIMIZE_CONVERSIONS: "Maximizar conversões",
    MAXIMIZE_CONVERSION_VALUE: "Maximizar valor de conversões",
    TARGET_CPA: "CPA alvo",
    TARGET_ROAS: "ROAS alvo",
    TARGET_IMPRESSION_SHARE: "Parcela de impressões alvo",
    TARGET_SPEND: "Maximizar cliques",
    MANUAL_CPC: "CPC manual",
    MANUAL_CPM: "CPM manual",
    MANUAL_CPV: "CPV manual",
    ENHANCED_CPC: "CPC otimizado",
    PERCENT_CPC: "CPC por porcentagem",
    COMMISSION: "Comissão",
  };
  return map[strategy] || strategy;
};

const matchTypeLabel = (matchType) => {
  const map = {
    EXACT: "Exata",
    PHRASE: "Frase",
    BROAD: "Ampla",
    BROAD_MATCH_MODIFIER: "Ampla modificada",
  };
  return map[matchType] || matchType;
};

const keywordStatusLabel = (status) => {
  const map = {
    ENABLED: "Ativa",
    PAUSED: "Pausada",
    REMOVED: "Removida",
  };
  return map[status] || status;
};

const ageRangeLabel = (range) => {
  const map = {
    AGE_RANGE_18_24: "18-24",
    AGE_RANGE_25_34: "25-34",
    AGE_RANGE_35_44: "35-44",
    AGE_RANGE_45_54: "45-54",
    AGE_RANGE_55_64: "55-64",
    AGE_RANGE_65_UP: "65+",
    AGE_RANGE_UNDETERMINED: "Indeterminado",
  };
  return map[range] || range;
};

const genderLabel = (gender) => {
  const map = {
    MALE: "Masculino",
    FEMALE: "Feminino",
    UNDETERMINED: "Indeterminado",
  };
  return map[gender] || gender;
};

const deviceLabel = (device) => {
  const map = {
    MOBILE: "Celular",
    DESKTOP: "Computador",
    TABLET: "Tablet",
    CONNECTED_TV: "Smart TV",
    OTHER: "Outros",
  };
  return map[device] || device;
};

const resourceTypeLabel = (resourceType) => {
  const map = {
    CAMPAIGN: "Campanha",
    CAMPAIGN_BUDGET: "Orçamento",
    AD_GROUP: "Grupo de anúncios",
    AD_GROUP_AD: "Anúncio",
    AD_GROUP_CRITERION: "Palavra-chave",
    AD_GROUP_BID_MODIFIER: "Modificador de lance",
    CAMPAIGN_CRITERION: "Critério de campanha",
    BIDDING_STRATEGY: "Estratégia de lance",
    FEED: "Feed",
    ASSET: "Ativo",
  };
  return map[resourceType] || resourceType;
};

const clientTypeLabel = (clientType) => {
  const map = {
    GOOGLE_ADS_WEB_CLIENT: "Painel Google Ads",
    GOOGLE_ADS_EDITOR: "Google Ads Editor",
    GOOGLE_ADS_API: "API Google Ads",
    GOOGLE_ADS_MOBILE_APP: "App Google Ads",
    GOOGLE_ADS_AUTOMATED_RULE: "Regra automatizada",
    GOOGLE_ADS_SCRIPTS: "Script Google Ads",
    GOOGLE_ADS_BULK_UPLOAD: "Upload em massa",
  };
  return map[clientType] || clientType;
};

const fieldLabel = (resourceType, field) => {
  const map = {
    "CAMPAIGN:status": "Status da campanha",
    "CAMPAIGN:maximizeConversions.targetCpaMicros": "Target CPA",
    "CAMPAIGN:maximizeConversionValue.targetRoas": "Target ROAS",
    "CAMPAIGN:targetCpa.targetCpaMicros": "Target CPA",
    "CAMPAIGN:targetRoas.targetRoas": "Target ROAS",
    "CAMPAIGN:bidding_strategy_type": "Estratégia de lance",
    "CAMPAIGN_BUDGET:amount_micros": "Orçamento diário",
    "AD_GROUP_AD:ad,status": "Status do anúncio",
    "AD_GROUP_AD:status": "Status do anúncio",
    "AD_GROUP_CRITERION:status": "Status da palavra-chave",
    "AD_GROUP_CRITERION:cpc_bid_micros": "Lance CPC",
    "AD_GROUP:status": "Status do grupo de anúncios",
    "AD_GROUP:cpc_bid_micros": "Lance CPC do grupo",
  };
  return (
    map[`${resourceType}:${field}`] ||
    `${resourceTypeLabel(resourceType)} · ${field}`
  );
};

const extractDiffValue = (jsonStr) => {
  if (!jsonStr) return "—";
  try {
    const obj = JSON.parse(jsonStr);
    const findValue = (o) => {
      if (o == null || typeof o !== "object") return o;
      for (const key of Object.keys(o)) {
        const val = o[key];
        if (typeof val === "object" && val !== null) {
          const nested = findValue(val);
          if (nested != null) return nested;
        } else if (val !== "" && val != null) {
          if (key === "status") return statusLabel(val);
          if (key === "targetCpaMicros") {
            const cpa = Number(val) / 1000000;
            return formatCurrency(cpa);
          }
          if (key === "amountMicros") {
            const amt = Number(val) / 1000000;
            return formatCurrency(amt);
          }
          return `${key}: ${val}`;
        }
      }
      return null;
    };
    const result = findValue(obj);
    return result != null ? String(result) : "—";
  } catch {
    return String(jsonStr).slice(0, 50);
  }
};

const WINDOWS = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "all", label: "Total" },
];

// ============================================================================
// Componente principal
// ============================================================================

export default function CampanhaAnalise() {
  const { external_id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeWindow, setActiveWindow] = useState("7d");
  const [activeSegment, setActiveSegment] = useState("keywords");
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analiseApi.get(`/campaign-data/${external_id}`);
      setData(response.data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Não foi possível carregar a análise";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (external_id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external_id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await analiseApi.post(`/refresh/${external_id}`, {
        days: 7,
      });
      toast.success(
        `Sincronizado: ${response.data.days_synced} dias, ${response.data.keywords_synced} palavras-chave`
      );
      await loadData();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Erro ao sincronizar";
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveNote = async (date, note) => {
    try {
      await analiseApi.post("/notes", { external_id, date, note });
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          daily: prev.daily.map((d) =>
            d.date === date ? { ...d, nota: note } : d
          ),
        };
      });
      toast.success("Nota salva", { autoClose: 1500 });
    } catch (err) {
      toast.error("Erro ao salvar nota");
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-12">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Carregando análise...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 md:p-6">
        <button
          onClick={() => navigate("/campanhas/analise")}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
          {error || "Dados não disponíveis"}
        </div>
      </div>
    );
  }

  const {
    campaign,
    totals,
    windows,
    daily,
    keywords,
    geo,
    devices,
    ages,
    genders,
    hours,
    changeHistory,
  } = data;

  const currencyCode = campaign?.currencyCode || "BRL";
  const currentWindow = windows?.[activeWindow] || {};

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">
        {/* ====================================================================
            1. HEADER
        ==================================================================== */}
        <div>
          <button
            onClick={() => navigate("/campanhas/analise")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 break-words">
                {campaign?.name || "—"}
              </h1>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-2">
                <span className="font-mono">ID: {campaign?.id}</span>
                <span>Status: {statusLabel(campaign?.status)}</span>
                {campaign?.biddingStrategy && (
                  <span>
                    Estratégia: {biddingStrategyLabel(campaign.biddingStrategy)}
                  </span>
                )}
                {campaign?.targetCpa != null && campaign.targetCpa > 0 && (
                  <span>CPA alvo: {formatCurrency(campaign.targetCpa, currencyCode)}</span>
                )}
                {campaign?.dailyBudget != null && campaign.dailyBudget > 0 && (
                  <span>Orçamento/dia: {formatCurrency(campaign.dailyBudget, currencyCode)}</span>
                )}
                {campaign?.account && <span>Conta: {campaign.account}</span>}
                {currencyCode && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[10px]">
                    {currencyCode}
                  </span>
                )}
                {campaign?.landingPage && (
                  <a
                    href={
                      campaign.landingPage.startsWith("http")
                        ? campaign.landingPage
                        : `https://${campaign.landingPage}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-100"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {campaign.landingPage}
                  </a>
                )}
                <span>
                  Última sync: {formatRelativeTime(campaign?.lastSyncedAt)}
                </span>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              {refreshing ? "Sincronizando..." : "Atualizar dados"}
            </button>
          </div>
        </div>

        {/* ====================================================================
            2. BANNER ACUMULADO
        ==================================================================== */}
        {totals && (
          <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 md:p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-4">
              Acumulado total
            </p>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              <BannerCell
                label="Custo total"
                value={formatCurrency(totals.custoAcumulado, currencyCode)}
              />
              <BannerCell
                label="Faturamento"
                value={formatCurrency(totals.faturamentoAcumulado, currencyCode)}
              />
              <BannerCell
                label="Resultado"
                value={formatCurrency(totals.resultadoAcumulado, currencyCode)}
                accent={
                  totals.resultadoAcumulado > 0
                    ? "positive"
                    : totals.resultadoAcumulado < 0
                    ? "negative"
                    : null
                }
              />
              <BannerCell label="CPA" value={formatCurrency(totals.cpaAcumulado, currencyCode)} />
              <BannerCell label="ROAS" value={formatDec(totals.roasAcumulado)} />
              <BannerCell
                label="Conversões"
                value={formatInt(totals.conversoesAcumuladas)}
              />
            </div>
          </section>
        )}

        {/* ====================================================================
            3. KPIs POR JANELA
        ==================================================================== */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 md:p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Indicadores por período
            </p>
            <WindowSelector value={activeWindow} onChange={setActiveWindow} />
          </div>

          <KpiRow title="Resultado financeiro">
            <KpiCard label="Custo" value={formatCurrency(currentWindow.custo, currencyCode)} />
            <KpiCard
              label="Faturamento"
              value={formatCurrency(currentWindow.faturamento, currencyCode)}
            />
            <KpiCard
              label="Resultado"
              value={formatCurrency(currentWindow.resultado, currencyCode)}
              accent={
                currentWindow.resultado > 0
                  ? "positive"
                  : currentWindow.resultado < 0
                  ? "negative"
                  : null
              }
            />
            <KpiCard label="ROAS" value={formatDec(currentWindow.roas)} />
          </KpiRow>

          <KpiRow title="Funil">
            <KpiCard label="Cliques" value={formatInt(currentWindow.cliques)} />
            <KpiCard
              label="Visitantes"
              value={formatInt(currentWindow.visitors)}
            />
            <KpiCard
              label="Conversões"
              value={formatInt(currentWindow.conversoes)}
            />
            <KpiCard
              label="CPA real"
              value={
                currentWindow.cpa != null ? formatCurrency(currentWindow.cpa, currencyCode) : "—"
              }
            />
          </KpiRow>

          <KpiRow title="Competitividade no leilão" last>
            <KpiCard
              label="Taxa de fuga"
              value={formatPct(currentWindow.taxaFuga)}
              accent={currentWindow.taxaFuga > 0.5 ? "warning" : null}
            />
            <KpiCard
              label="Parc. impressões"
              value={formatPct(currentWindow.parcImpressoes)}
            />
            <KpiCard
              label="CPC médio"
              value={formatCurrency(currentWindow.cpcMedio, currencyCode)}
            />
            <KpiCard label="CTR" value={formatPct(currentWindow.ctr)} />
          </KpiRow>
        </section>

        {/* ====================================================================
            4. CPC IDEAL
        ==================================================================== */}
        <CpcIdealPanel window={currentWindow} currencyCode={currencyCode} />

        {/* ====================================================================
            5. TABELA DIÁRIA
        ==================================================================== */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              Tabela diária
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Clique na coluna "Notas" pra adicionar anotações sobre o dia.
            </p>
          </div>
          <DailyTable daily={daily || []} onSaveNote={handleSaveNote} currencyCode={currencyCode} />
        </section>

        {/* ====================================================================
            6. SEGMENTAÇÕES
        ==================================================================== */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4">
              Segmentações
            </h2>
            <SegmentTabs active={activeSegment} onChange={setActiveSegment} />
          </div>

          <div className="overflow-x-auto">
            {activeSegment === "keywords" && (
              <KeywordsTable keywords={keywords || []} currencyCode={currencyCode} />
            )}
            {activeSegment === "geo" && <GeoTable geo={geo || []} currencyCode={currencyCode} />}
            {activeSegment === "devices" && (
              <DevicesTable devices={devices || []} currencyCode={currencyCode} />
            )}
            {activeSegment === "demographics" && (
              <DemographicsTable ages={ages || []} genders={genders || []} currencyCode={currencyCode} />
            )}
            {activeSegment === "hours" && <HoursTable hours={hours || []} currencyCode={currencyCode} />}
          </div>
        </section>

        {/* ====================================================================
            7. HISTÓRICO DE ALTERAÇÕES
        ==================================================================== */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
          >
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                Histórico de alterações
              </h2>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                ({changeHistory?.length || 0} eventos)
              </span>
            </div>
            {historyOpen ? (
              <ChevronUp className="w-5 h-5 text-slate-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-500" />
            )}
          </button>
          {historyOpen && <ChangeHistoryList history={changeHistory || []} />}
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// Auxiliares
// ============================================================================

function BannerCell({ label, value, accent }) {
  const accentClasses =
    accent === "positive"
      ? "text-emerald-600 dark:text-emerald-400"
      : accent === "negative"
      ? "text-red-600 dark:text-red-400"
      : "text-slate-900 dark:text-slate-100";
  return (
    <div>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-xl md:text-2xl font-bold ${accentClasses}`}>
        {value}
      </p>
    </div>
  );
}

function KpiRow({ title, children, last }) {
  return (
    <div className={last ? "" : "mb-5"}>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
        {title}
      </p>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">{children}</div>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  const accentClasses =
    accent === "positive"
      ? "text-emerald-600 dark:text-emerald-400"
      : accent === "negative"
      ? "text-red-600 dark:text-red-400"
      : accent === "warning"
      ? "text-red-600 dark:text-red-400"
      : "text-slate-900 dark:text-slate-100";
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 p-3">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-lg font-bold ${accentClasses}`}>{value}</p>
    </div>
  );
}

function WindowSelector({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800/40">
      {WINDOWS.map((w) => (
        <button
          key={w.key}
          onClick={() => onChange(w.key)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition ${
            value === w.key
              ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

function CpcIdealPanel({ window: w, currencyCode = "BRL" }) {
  const visitors = w.visitors || 0;
  const conversoes = w.conversoes || 0;
  const faturamento = w.faturamento || 0;
  const cliques = w.cliques || 0;

  if (!visitors || !conversoes || !cliques) {
    return (
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-2">
          CPC ideal por margem
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Sem dados suficientes nesta janela pra calcular.
        </p>
      </section>
    );
  }

  const convRate = conversoes / cliques;
  const ticketMedio = faturamento / conversoes;
  const cpcEmpatar = convRate * ticketMedio;

  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 md:p-6">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 mb-4">
        CPC ideal por margem de lucro
      </p>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Empatar (0%)" value={formatCurrency(cpcEmpatar, currencyCode)} />
        <KpiCard label="25% lucro" value={formatCurrency(cpcEmpatar * 0.75, currencyCode)} />
        <KpiCard label="50% lucro" value={formatCurrency(cpcEmpatar * 0.5, currencyCode)} />
        <KpiCard label="75% lucro" value={formatCurrency(cpcEmpatar * 0.25, currencyCode)} />
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
        Baseado na taxa de conversão por clique e ticket médio da janela
        selecionada.
      </p>
    </section>
  );
}

function DailyTable({ daily, onSaveNote, currencyCode = "BRL" }) {
  const [editingDate, setEditingDate] = useState(null);
  const [draftNote, setDraftNote] = useState("");

  if (!daily || daily.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Sem dados diários disponíveis.
      </div>
    );
  }

  const startEdit = (row) => {
    setEditingDate(row.date);
    setDraftNote(row.nota || "");
  };

  const commitEdit = () => {
    if (editingDate) onSaveNote(editingDate, draftNote);
    setEditingDate(null);
    setDraftNote("");
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400">
          <tr>
            <th className="text-left py-2.5 px-3 font-medium">Data</th>
            <th className="text-right py-2.5 px-2 font-medium">Impr.</th>
            <th className="text-right py-2.5 px-2 font-medium">Cliques</th>
            <th className="text-right py-2.5 px-2 font-medium">Visitas</th>
            <th className="text-right py-2.5 px-2 font-medium">Cl. Pág.</th>
            <th className="text-right py-2.5 px-2 font-medium">Checkouts</th>
            <th className="text-right py-2.5 px-2 font-medium">Conv.</th>
            <th className="text-right py-2.5 px-2 font-medium">P. Imp.</th>
            <th className="text-right py-2.5 px-2 font-medium">Imp. Sup.</th>
            <th className="text-right py-2.5 px-2 font-medium">Imp. 1ª</th>
            <th className="text-right py-2.5 px-2 font-medium">CTR</th>
            <th className="text-right py-2.5 px-2 font-medium">CPC</th>
            <th className="text-right py-2.5 px-2 font-medium">Custo</th>
            <th className="text-right py-2.5 px-2 font-medium">Orçam./dia</th>
            <th className="text-right py-2.5 px-2 font-medium">CPA alvo</th>
            <th className="text-right py-2.5 px-2 font-medium">Faturam.</th>
            <th className="text-right py-2.5 px-2 font-medium">Fuga</th>
            <th className="text-right py-2.5 px-2 font-medium">CPA real</th>
            <th className="text-right py-2.5 px-2 font-medium">ROAS</th>
            <th className="text-right py-2.5 px-2 font-medium">Result.</th>
            <th className="text-left py-2.5 px-3 font-medium w-[200px]">
              Notas
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {daily.map((row) => (
            <tr
              key={row.date}
              className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
            >
              <td className="py-2 px-3 text-slate-900 dark:text-slate-100 font-medium">
                {formatDate(row.date)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatInt(row.impressoes)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatInt(row.cliques)}
              </td>
              {/* Visitas (tracker) */}
              <td className="py-2 px-2 text-right tabular-nums">
                {formatInt(row.visitors)}
              </td>
              {/* Cliques na página = visitors × (1 - taxaFuga) */}
              <td className="py-2 px-2 text-right tabular-nums">
                {row.visitors > 0
                  ? formatInt(Math.round(row.visitors * (1 - (row.taxaFuga || 0))))
                  : "—"}
              </td>
              {/* Checkouts */}
              <td className="py-2 px-2 text-right tabular-nums">
                {row.checkouts > 0 ? formatInt(row.checkouts) : "—"}
              </td>
              {/* Conversões */}
              <td className="py-2 px-2 text-right tabular-nums">
                {formatInt(row.conversoes)}
              </td>
              {/* Parc. impressões */}
              <td className="py-2 px-2 text-right tabular-nums">
                {formatPct(row.parcImpressoes)}
              </td>
              {/* Imp. parte superior */}
              <td className="py-2 px-2 text-right tabular-nums">
                {formatPct(row.parcSuperior)}
              </td>
              {/* Imp. 1ª posição */}
              <td className="py-2 px-2 text-right tabular-nums">
                {formatPct(row.parcAbsTop)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatPct(row.ctr)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatCurrency(row.cpcMedio, currencyCode)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatCurrency(row.custo, currencyCode)}
              </td>
              {/* Orçamento diário do dia */}
              <td className="py-2 px-2 text-right tabular-nums text-slate-400">
                {row.dailyBudget != null && row.dailyBudget > 0
                  ? formatCurrency(row.dailyBudget, currencyCode)
                  : "—"}
              </td>
              {/* CPA alvo do dia */}
              <td className="py-2 px-2 text-right tabular-nums text-slate-400">
                {row.targetCpa != null && row.targetCpa > 0
                  ? formatCurrency(row.targetCpa, currencyCode)
                  : "—"}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatCurrency(row.faturamento, currencyCode)}
              </td>
              <td
                className={`py-2 px-2 text-right tabular-nums ${
                  row.taxaFuga > 0.5 ? "text-red-600 dark:text-red-400" : ""
                }`}
              >
                {formatPct(row.taxaFuga)}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {row.cpaReal != null && row.cpaReal > 0
                  ? formatCurrency(row.cpaReal, currencyCode)
                  : "—"}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">
                {formatDec(row.roas)}
              </td>
              <td
                className={`py-2 px-2 text-right tabular-nums font-semibold ${
                  row.resultado < 0
                    ? "text-red-600 dark:text-red-400"
                    : row.resultado > 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : ""
                }`}
              >
                {formatCurrency(row.resultado, currencyCode)}
              </td>
              <td className="py-1.5 px-3">
                {editingDate === row.date ? (
                  <input
                    autoFocus
                    type="text"
                    value={draftNote}
                    onChange={(e) => setDraftNote(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitEdit();
                      if (e.key === "Escape") {
                        setEditingDate(null);
                        setDraftNote("");
                      }
                    }}
                    className="w-full px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100"
                    placeholder="Anotação..."
                  />
                ) : (
                  <button
                    onClick={() => startEdit(row)}
                    className="w-full text-left px-2 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1.5 group/btn"
                  >
                    <span
                      className={
                        row.nota
                          ? "text-slate-700 dark:text-slate-200 truncate"
                          : "text-slate-400 italic"
                      }
                    >
                      {row.nota || "Adicionar nota..."}
                    </span>
                    <Edit3 className="w-3 h-3 text-slate-300 opacity-0 group-hover/btn:opacity-100 ml-auto flex-shrink-0" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SEGMENT_TABS = [
  { key: "keywords", label: "Palavras-chave", icon: Search },
  { key: "devices", label: "Dispositivos", icon: Smartphone },
  { key: "demographics", label: "Idade & Sexo", icon: Users },
  { key: "hours", label: "Horários", icon: Clock },
];

function SegmentTabs({ active, onChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {SEGMENT_TABS.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              active === tab.key
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function KeywordsTable({ keywords, currencyCode = "BRL" }) {
  if (!keywords.length) {
    return (
      <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Sem palavras-chave disponíveis.
      </div>
    );
  }
  return (
    <table className="w-full text-xs">
      <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400">
        <tr>
          <th className="text-left py-2.5 px-3 font-medium">Palavra-chave</th>
          <th className="text-left py-2.5 px-2 font-medium">Tipo</th>
          <th className="text-left py-2.5 px-2 font-medium">Status</th>
          <th className="text-right py-2.5 px-2 font-medium">Qual.</th>
          <th className="text-right py-2.5 px-2 font-medium">CTR esp.</th>
          <th className="text-right py-2.5 px-2 font-medium">Relev.</th>
          <th className="text-right py-2.5 px-2 font-medium">Página</th>
          <th className="text-right py-2.5 px-2 font-medium">Cliques</th>
          <th className="text-right py-2.5 px-2 font-medium">CPC</th>
          <th className="text-right py-2.5 px-2 font-medium">Custo</th>
          <th className="text-right py-2.5 px-2 font-medium">Checkouts</th>
          <th className="text-right py-2.5 px-2 font-medium">Conv.</th>
          <th className="text-right py-2.5 px-2 font-medium">Valor conv.</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {keywords.map((kw, i) => (
          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
            <td className="py-2 px-3 text-slate-900 dark:text-slate-100">
              {kw.text || "—"}
            </td>
            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 text-[11px]">
              {matchTypeLabel(kw.matchType) || "—"}
            </td>
            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 text-[11px]">
              {keywordStatusLabel(kw.status) || "—"}
            </td>
            <td className="py-2 px-2 text-right">
              <QualityScore score={kw.qualityScore} />
            </td>
            <td className="py-2 px-2 text-right">
              <QualityComponentBadge value={kw.expectedCtr} />
            </td>
            <td className="py-2 px-2 text-right">
              <QualityComponentBadge value={kw.adRelevance} />
            </td>
            <td className="py-2 px-2 text-right">
              <QualityComponentBadge value={kw.landingPageExp} />
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {formatInt(kw.cliques)}
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {formatCurrency(kw.cpcMedio, currencyCode)}
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {formatCurrency(kw.custo, currencyCode)}
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {kw.tracker_checkouts > 0 ? formatInt(kw.tracker_checkouts) : "—"}
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {formatInt(kw.tracker_conversions ?? kw.ads_conversions ?? kw.conversoes ?? kw.conversions)}
            </td>
            <td className="py-2 px-2 text-right tabular-nums">
              {kw.conversoes_value != null && kw.conversoes_value > 0
                ? formatCurrency(kw.conversoes_value, currencyCode)
                : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QualityScore({ score }) {
  if (score == null) return <span className="text-slate-400">—</span>;
  const color =
    score >= 7
      ? "text-emerald-600 dark:text-emerald-400"
      : score >= 4
      ? "text-slate-600 dark:text-slate-300"
      : "text-red-600 dark:text-red-400";
  return <span className={`font-semibold ${color}`}>{score}/10</span>;
}

function QualityComponentBadge({ value }) {
  if (!value) return <span className="text-slate-400 text-xs">—</span>;
  const map = {
    ABOVE_AVERAGE: {
      color: "text-emerald-600 dark:text-emerald-400",
      label: "Acima",
    },
    AVERAGE: {
      color: "text-slate-600 dark:text-slate-300",
      label: "Médio",
    },
    BELOW_AVERAGE: {
      color: "text-red-600 dark:text-red-400",
      label: "Abaixo",
    },
  };
  const config = map[value] || { color: "text-slate-400", label: value };
  return <span className={`text-xs ${config.color}`}>{config.label}</span>;
}

function SimpleSegmentTable({ rows, labelHeader, getLabel, columns, currencyCode = "BRL" }) {
  if (!rows.length) {
    return (
      <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Sem dados disponíveis.
      </div>
    );
  }
  const cols = columns || [
    { key: "cliques", label: "Cliques", get: (r) => r.cliques ?? r.clicks },
    { key: "ctr", label: "CTR", get: (r) => r.ctr, format: formatPct },
    { key: "custo", label: "Custo", get: (r) => r.custo, format: (v) => formatCurrency(v, currencyCode) },
    { key: "visitors", label: "Visitantes", get: (r) => r.visitors },
    { key: "checkouts", label: "Checkouts", get: (r) => r.tracker_checkouts ?? r.checkouts },
    {
      key: "conversoes",
      label: "Conv.",
      get: (r) => r.tracker_conversions ?? r.ads_conversions ?? r.conversoes ?? r.conversions,
    },
  ];
  return (
    <table className="w-full text-xs">
      <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400">
        <tr>
          <th className="text-left py-2.5 px-3 font-medium">{labelHeader}</th>
          {cols.map((c) => (
            <th key={c.key} className="text-right py-2.5 px-2 font-medium">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
            <td className="py-2 px-3 text-slate-900 dark:text-slate-100">
              {getLabel(row)}
            </td>
            {cols.map((c) => {
              const v = c.get(row);
              const fmt = c.format || formatInt;
              return (
                <td key={c.key} className="py-2 px-2 text-right tabular-nums">
                  {fmt(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GeoTable({ geo, currencyCode = "BRL" }) {
  return (
    <SimpleSegmentTable
      rows={geo}
      labelHeader="Localização"
      getLabel={(r) => r.label || r.city || r.region || r.country || "—"}
      currencyCode={currencyCode}
    />
  );
}

function DevicesTable({ devices, currencyCode = "BRL" }) {
  return (
    <SimpleSegmentTable
      rows={devices}
      labelHeader="Dispositivo"
      getLabel={(r) => deviceLabel(r.device || r.label)}
      currencyCode={currencyCode}
    />
  );
}

function HoursTable({ hours, currencyCode = "BRL" }) {
  const sorted = [...hours].sort((a, b) => (a.hour || 0) - (b.hour || 0));
  return (
    <SimpleSegmentTable
      rows={sorted}
      labelHeader="Hora"
      getLabel={(r) => `${String(r.hour).padStart(2, "0")}:00`}
      currencyCode={currencyCode}
      columns={[
        { key: "cliques", label: "Cliques", get: (r) => r.cliques },
        { key: "custo", label: "Custo", get: (r) => r.custo, format: (v) => formatCurrency(v, currencyCode) },
        { key: "visitors", label: "Visitantes", get: (r) => r.visitors },
        { key: "checkouts", label: "Checkouts", get: (r) => r.tracker_checkouts ?? r.checkouts },
        {
          key: "conversoes",
          label: "Conv.",
          get: (r) => r.tracker_conversions ?? r.ads_conversions ?? r.conversoes,
        },
      ]}
    />
  );
}

function DemographicsTable({ ages, genders, currencyCode = "BRL" }) {
  if (!ages.length && !genders.length) {
    return (
      <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Sem dados demográficos disponíveis.
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100 dark:divide-slate-800">
      <div>
        <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
          Faixa etária
        </p>
        <SimpleSegmentTable
          rows={ages}
          labelHeader="Idade"
          getLabel={(r) => ageRangeLabel(r.range || r.label)}
          currencyCode={currencyCode}
          columns={[
            { key: "cliques", label: "Cliques", get: (r) => r.cliques },
            { key: "custo", label: "Custo", get: (r) => r.custo, format: (v) => formatCurrency(v, currencyCode) },
            { key: "visitors", label: "Visitantes", get: (r) => r.visitors },
            { key: "checkouts", label: "Checkouts", get: (r) => r.tracker_checkouts ?? r.checkouts },
            {
              key: "conversoes",
              label: "Conv.",
              get: (r) => r.tracker_conversions ?? r.ads_conversions ?? r.conversoes,
            },
          ]}
        />
      </div>
      <div>
        <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
          Sexo
        </p>
        <SimpleSegmentTable
          rows={genders}
          labelHeader="Sexo"
          getLabel={(r) => genderLabel(r.gender || r.label)}
          currencyCode={currencyCode}
          columns={[
            { key: "cliques", label: "Cliques", get: (r) => r.cliques },
            { key: "custo", label: "Custo", get: (r) => r.custo, format: (v) => formatCurrency(v, currencyCode) },
            { key: "visitors", label: "Visitantes", get: (r) => r.visitors },
            { key: "checkouts", label: "Checkouts", get: (r) => r.tracker_checkouts ?? r.checkouts },
            {
              key: "conversoes",
              label: "Conv.",
              get: (r) => r.tracker_conversions ?? r.ads_conversions ?? r.conversoes,
            },
          ]}
        />
      </div>
    </div>
  );
}

function ChangeHistoryList({ history }) {
  if (!history.length) {
    return (
      <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800">
        Sem alterações registradas.
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800 border-t border-slate-100 dark:border-slate-800">
      {history.map((evt, i) => {
        const oldVal = extractDiffValue(evt.oldValue);
        const newVal = extractDiffValue(evt.newValue);
        return (
          <div key={i} className="px-5 py-3 flex items-start gap-3 text-xs">
            <div className="text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap font-mono w-[110px] shrink-0">
              {formatDateTime(evt.date)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-900 dark:text-slate-100 font-medium">
                {fieldLabel(evt.resourceType, evt.field)}
                {evt.detail && (
                  <span className="text-slate-500 dark:text-slate-400 font-normal">
                    {" "}
                    · {evt.detail}
                  </span>
                )}
              </div>
              <div className="text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="line-through text-red-500/70 dark:text-red-400/70 truncate max-w-[200px]">
                  {oldVal}
                </span>
                <span>→</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium truncate max-w-[200px]">
                  {newVal}
                </span>
                <span className="text-slate-400">·</span>
                <span className="truncate">
                  {evt.user}
                  {evt.clientType && (
                    <span className="text-slate-400">
                      {" "}
                      · {clientTypeLabel(evt.clientType)}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}